import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { TokenService } from './token.service';
import { ListResponse } from '../../models/list-response.model';
import { OrderModel } from '../../models/order.model';
import { OrderCountModel } from '../../models/order-count.model';
import { ProductModel } from '../../models/product.model';
import { ProductOrderCountModel } from '../../models/product-order-count.model';
import {
  PostFinalizationCountBody,
  PostFinalizationCountResponse,
} from '../../models/finalization-count.model';

@Injectable({
  providedIn: 'root',
})
export class CountService {
  apiUrl = environment.API_URL + 'internal/';

  constructor(
    private httpClient: HttpClient,
    private tokenService: TokenService,
  ) {}

  getCustomerHeader() {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.tokenService.getComponentCodeToken(),
      'Accept-Language': 'pt-BR',
    });
  }

  public postConferenceCountItem(
    branchCode: number,
    code: number,
    productCode: number,
    barCode: string,
    quantity: number = 1,
  ): Observable<any> {
    const url = this.apiUrl + 'product-count/v2/counts-item';

    const body = {
      branchCode,
      code,
      productCode,
      barCode,
      quantity,
    };

    return this.httpClient.post<any>(url, body, {
      headers: this.getCustomerHeader(),
    });
  }

  public getTerminalList(BranchCode: number): Observable<any> {
    let params = new HttpParams().set('BranchCode', BranchCode);
    return this.httpClient.get<any>(this.apiUrl + 'general/v2/terminals', {
      params: params,
      headers: this.getCustomerHeader(),
    });
  }

  public getProduct(BarCode: any, BranchCode: number): Observable<ListResponse<ProductModel>> {
    return this.httpClient.get<any>(
      this.apiUrl + 'product/v2/products/' + BarCode + '/' + BranchCode,
      { headers: this.getCustomerHeader() },
    );
  }

  public getOrdersHeader(
    terminalBranchCode: number,
    terminalCode: number,
    orderCode: number | null = null,
    page: number = 1,
    pageSize: number = 1000,
  ): Observable<ListResponse<OrderModel>> {
    const url = `${this.apiUrl}product-count/v2/order`;

    let params = new HttpParams()
      .set('TerminalBranchCode', terminalBranchCode)
      .set('TerminalCode', terminalCode)
      .set('Page', page)
      .set('PageSize', pageSize);

    if (orderCode != null) {
      params = params.set('orderCode', orderCode);
    }

    return this.httpClient.get<ListResponse<OrderModel>>(url, {
      params: params,
      headers: this.getCustomerHeader(),
    });
  }

  public getCountList(
    TerminalBranchCode: number,
    TerminalCode: number,
    OrderCode: number,
    Page: number = 1,
    PageSize: number = 1000,
  ): Observable<ListResponse<OrderCountModel>> {
    let params = new HttpParams()
      .set('TerminalBranchCode', TerminalBranchCode)
      .set('TerminalCode', TerminalCode)
      .set('OrderCode', OrderCode)
      .set('Page', Page)
      .set('PageSize', PageSize);

    return this.httpClient.get<any>(this.apiUrl + 'product-count/v2/countable-orders', {
      params: params,
      headers: this.getCustomerHeader(),
    });
  }

  public getSalesOrderHeadersCount(
    OrderBranchCode: number,
    OrderCode: number,
    CountBranchCode: number,
    Page: number,
    PageSize: number,
  ): Observable<any> {
    let params = new HttpParams()
      .set('OrderBranchCode', OrderBranchCode)
      .set('OrderCode', OrderCode)
      .set('CountBranchCode', CountBranchCode)
      .set('Page', Page)
      .set('PageSize', PageSize);

    return this.httpClient.get<any>(this.apiUrl + 'product-count/v2/sales-order-headers-count', {
      params: params,
      headers: this.getCustomerHeader(),
    });
  }

  public getOrderItemsCount(
    BranchCode: number,
    OrderCode: number,
    CountCode: number,
  ): Observable<any> {
    let params = new HttpParams()
      .set('BranchCode', BranchCode)
      .set('OrderCode', OrderCode)
      .set('CountCode', CountCode);

    return this.httpClient.get<any>(this.apiUrl + 'product-count/v2/order-items-count', {
      params: params,
      headers: this.getCustomerHeader(),
    });
  }

  public postProductItensCount(
    terminalBranchCode: number,
    terminalCode: number,
    branchCode: number,
    countCode: number,
    productCode: number,
    countQuantity: number,
    pendingQuantity: number,
    overCountQuantity: number,
    orderCode: number,
    orderQuantity: number,
    barCode: string,
    rfId: string | null = null,
  ): Observable<ListResponse<ProductOrderCountModel>> {
    let data = {
      TerminalBranchCode: terminalBranchCode,
      TerminalCode: terminalCode,
      branchCode: branchCode,
      countCode: countCode,
      orderCode: orderCode,
      items: [
        {
          productCode: productCode,
          countQuantity: countQuantity,
          pendingQuantity: pendingQuantity,
          overCountQuantity: overCountQuantity,
          orderQuantity: orderQuantity,
          barCode: barCode,
          rfId: rfId,
        },
      ],
    };

    return this.httpClient.post<any>(this.apiUrl + 'product-count/v2/itens-count', data, {
      headers: this.getCustomerHeader(),
    });
  }

  public deleteProductItensCount(
    branchCode: number,
    countCode: number,
    productsToDelete: { productCode: number; rfid: string }[],
  ): Observable<any> {
    const data = {
      branchCode: branchCode,
      countCode: countCode,
      items: productsToDelete,
    };

    return this.httpClient.request('DELETE', this.apiUrl + 'product-count/v2/itens-count', {
      body: data,
      headers: this.getCustomerHeader(),
    });
  }

  public postFinalizeSalesOrderCount(
    salesOrderBranchCode: number,
    countCode: number,
    countBranchCode: number,
    salesOrderCode: number,
  ): Observable<any> {
    let data = {
      salesOrderBranchCode: salesOrderBranchCode,
      countCode: countCode,
      countBranchCode: countBranchCode,
      salesOrderCode: salesOrderCode,
    };
    return this.httpClient.post<any>(
      this.apiUrl + 'product-count/v2/finalize-sales-order-count',
      data,
      { headers: this.getCustomerHeader() },
    );
  }

  public postCreateOrderCount(
    branchCode: number,
    terminalCode: number,
    orderCode: number,
    orderSequence: number,
    terminalBranchCode: number,
    description: string,
  ): Observable<any> {
    let data: any = {
      branchCode: branchCode,
      terminalCode: terminalCode,
      orderCode: orderCode,
      orderSequence: orderSequence,
      terminalBranchCode: terminalBranchCode,
      description: description,
      component: 'GERAPP001',
    };

    return this.httpClient.post<any>(
      this.apiUrl + 'product-count/v2/initiate-sales-order-count',
      data,
      { headers: this.getCustomerHeader() },
    );
  }

  public postCount(branchCode: number, description: string, component: string): Observable<any> {
    const data = {
      branchCode: branchCode,
      description: description,
      component: component,
    };

    return this.httpClient.post<any>(this.apiUrl + 'product-count/v2/counts', data, {
      headers: this.getCustomerHeader(),
    });
  }

  public getPendingOutputCounts(branchCode: number, component: string): Observable<any> {
    const params = new HttpParams()
      .set('BranchCode', branchCode)
      .append('Status', 1)
      .append('Status', 11)
      .set('User', this.tokenService.getUserCode()!)
      .set('Component', component);

    return this.httpClient.get<any>(this.apiUrl + 'product-count/v2/output-counts', {
      params,
      headers: this.getCustomerHeader(),
    });
  }

  public getCountByCode(branchCode: number, code: number): Observable<any> {
    const params = new HttpParams().set('BranchCode', branchCode).set('Code', code);

    return this.httpClient.get<any>(this.apiUrl + 'product-count/v2/production-order/counts', {
      params,
      headers: this.getCustomerHeader(),
    });
  }

  public postCountItem(
    branchCode: number,
    code: number,
    productCode: number,
    barCode: string,
    quantity: number,
    productionOrderBranchCode?: number,
    productionOrderCycleCode?: number,
    productionOrderCode?: number,
    // pack: number
  ): Observable<any> {
    const url = this.apiUrl + 'product-count/v2/production-order/counts-item';

    const body = {
      branchCode,
      code,
      productCode,
      barCode,
      quantity,
      productionOrderBranchCode,
      productionOrderCycleCode,
      productionOrderCode,
    };

    return this.httpClient.post<any>(url, body, {
      headers: this.getCustomerHeader(),
    });
  }

  public deleteCountItem(branchCode: number, code: number, sequence: number): Observable<any> {
    const params = new HttpParams()
      .set('BranchCode', branchCode)
      .set('Code', code)
      .set('Sequence', sequence);
    // .set('Pack', Pack);

    const url = this.apiUrl + 'product-count/v2/counts-item';

    return this.httpClient.delete<any>(url, {
      headers: this.getCustomerHeader(),
      params: params,
    });
  }

  public postUpdateCountStatus(branchCode: number, code: number, status: number): Observable<any> {
    const url = `${this.apiUrl}product-count/v2/counts-status`;

    const body = {
      branchCode: branchCode,
      code: code,
      Status: status,
    };

    return this.httpClient.post<any>(url, body, {
      headers: this.getCustomerHeader(),
    });
  }

  public postCountAdditional(
    branchCode: number,
    code: number,
    additional: {
      placeOrigin: number;
      placeDestination: number;
    },
  ): Observable<any> {
    const url = this.apiUrl + 'product-count/v2/counts-additional';

    const body = {
      branchCode,
      code,
      additional,
    };

    return this.httpClient.post<any>(url, body, {
      headers: this.getCustomerHeader(),
    });
  }

  public getOutputCountAdditionals(branchCode: number, code: number): Observable<any> {
    const params = new HttpParams().set('BranchCode', branchCode).set('Code', code);

    const url = this.apiUrl + 'product-count/v2/output-countadditionals';

    return this.httpClient.get<any>(url, {
      params: params,
      headers: this.getCustomerHeader(),
    });
  }

  public postFinalizationCount(
    body: PostFinalizationCountBody,
  ): Observable<PostFinalizationCountResponse> {
    const cleanedBody = this.removeNullValues(body);
    return this.httpClient.post<PostFinalizationCountResponse>(
      this.apiUrl + `product-count/v2/production-order/counts`,
      cleanedBody,
      { headers: this.getCustomerHeader() },
    );
  }

  private removeNullValues(obj: any): any {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([_, value]) => value !== null && value !== undefined && value !== '',
      ),
    );
  }
}
